import { api } from './client';
import type { Page } from './catalogs';

export type VatAppendixCode = 'D1' | 'D2' | 'D3' | 'D4' | 'D5' | 'D6';

/** Рядок таблиці додатка: {код графи (G2, G3D…): значення}. */
export type VatAppendixRow = Record<string, string | number | null>;

/** Дані бланка додатка: скалярні поля + таблиці (ключі — data_key специфікації). */
export interface VatAppendixData {
  fields?: Record<string, string | number | null>;
  [tableKey: string]: unknown;
}

/** Специфікація бланка додатка (з бекенда — єдине джерело). */
export interface VatAppendixSpec {
  title: string;
  auto: boolean;
  fields: { code: string; label: string; type: string }[];
  tables: {
    key: string;
    data_key: string;
    label: string;
    cols: { code: string; label: string; type: string; width?: number }[];
    totals: Record<string, string>;
  }[];
}

/** Декларація з ПДВ (J0200126). values: {код поля XSD: ціле грн}. */
export interface VatDeclaration {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  report_type: 'REPORTING' | 'REPORTING_NEW' | 'CORRECTING';
  period_year: number;
  period_month: number;
  doc_cnt: number;
  values: Record<string, number>;
  appendices: VatAppendixCode[];
  appendix_data: Record<string, VatAppendixData>;
  // Уточнюючий розрахунок: період, що уточнюється (HZYP/HZMP).
  corrected_period_year: number | null;
  corrected_period_month: number | null;
  comment: string | null;
  deletion_mark: boolean;
  created_at: string;
}

export interface VatDeclCheck {
  errors: { form: string; message: string }[];
  warnings: { form: string; message: string }[];
  included_appendices: string[];
}

export const vatDeclarations = {
  list: (params?: {
    organization_id?: string; period_year?: number; period_month?: number;
    sort_by?: string; sort_dir?: 'asc' | 'desc'; limit?: number; offset?: number;
  }) =>
    api.get<Page<VatDeclaration>>('/vat-declarations', { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<VatDeclaration>(`/vat-declarations/${id}`).then((r) => r.data),
  // Специфікації бланків додатків Д1–Д6 (поля/таблиці/підписи).
  appendixSpecs: () =>
    api.get<Record<string, VatAppendixSpec>>('/vat-declarations/appendix-specs')
      .then((r) => r.data),
  create: (p: {
    organization_id: string; period_year: number; period_month: number;
    report_type?: string; comment?: string | null;
    appendices?: VatAppendixCode[];
    corrected_period_year?: number;
    corrected_period_month?: number;
  }) =>
    api.post<VatDeclaration>('/vat-declarations', p).then((r) => r.data),
  update: (id: string, p: {
    report_type?: string; comment?: string | null;
    document_date?: string;
    values?: Record<string, number>;
    appendices?: VatAppendixCode[];
    appendix_data?: Record<string, VatAppendixData>;
  }) =>
    api.patch<VatDeclaration>(`/vat-declarations/${id}`, p).then((r) => r.data),
  recalc: (id: string) =>
    api.post<VatDeclaration>(`/vat-declarations/${id}/recalc`).then((r) => r.data),
  check: (id: string) =>
    api.get<VatDeclCheck>(`/vat-declarations/${id}/check`).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/vat-declarations/${id}`).then(() => undefined),
  // Повна axios-відповідь: ім'я файлу ДПС — з Content-Disposition.
  xml: (id: string) =>
    api.get(`/vat-declarations/${id}/xml`, { responseType: 'blob' }),
  // Пакет (декларація + додатки): fname + base64 кожного файлу для підпису.
  files: (id: string) =>
    api.get<{ files: { fname: string; xml_base64: string }[] }>(
      `/vat-declarations/${id}/files`).then((r) => r.data),
  submission: (id: string) =>
    api.get<{ submission_id: string | null; status: string | null;
      receipt_accepted?: boolean | null }>(
      `/vat-declarations/${id}/submission`).then((r) => r.data),
};
